import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api'

export interface User {
  id: string
  email: string
  name: string
  picture_url: string | null
  role: 'owner' | 'property_manager' | 'family' | 'friend' | 'guest'
  can_book: boolean
  can_approve: boolean
  auth_provider: 'google' | 'facebook' | 'github' | 'discord'
  coc_agreed_at: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)

  const isAuthenticated = computed(() => user.value !== null)
  const isOwner = computed(() => user.value?.role === 'owner')
  const canBook = computed(() => user.value?.can_book || user.value?.role === 'owner')
  const canApprove = computed(() => user.value?.can_approve || user.value?.role === 'owner')
  const needsInvite = computed(() => user.value?.role === 'guest')
  const hasAgreedToCoc = computed(() => user.value?.coc_agreed_at !== null)
  const canManageInventory = computed(() => user.value?.role === 'owner' || user.value?.role === 'property_manager')
  const canViewInventory = computed(() => user.value?.role !== 'guest')
  const canUpdateConsumables = computed(() => user.value?.role === 'owner' || user.value?.role === 'property_manager' || user.value?.role === 'family')

  async function checkAuth() {
    loading.value = true
    try {
      const response = await api.get('/api/auth/me')
      if (response.authenticated) {
        user.value = response.user
      } else {
        user.value = null
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      user.value = null
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/api/auth/logout')
    } finally {
      user.value = null
      localStorage.removeItem('session_token')
      window.location.href = '/login'
    }
  }

  async function redeemInvite(code: string) {
    const response = await api.post('/api/auth/invite', { code })
    if (response.success) {
      // Refresh user data
      await checkAuth()
      return true
    }
    return false
  }

  function loginWithGoogle() {
    const apiBase = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiBase}/api/auth/google`
  }

  function loginWithFacebook() {
    const apiBase = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiBase}/api/auth/facebook`
  }

  function loginWithGitHub() {
    const apiBase = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiBase}/api/auth/github`
  }

  function loginWithDiscord() {
    const apiBase = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiBase}/api/auth/discord`
  }

  async function agreeToCoc() {
    const response = await api.post('/api/users/agree-coc')
    if (response.success) {
      // Refresh user data to get updated coc_agreed_at
      await checkAuth()
      return true
    }
    return false
  }

  return {
    user,
    loading,
    isAuthenticated,
    isOwner,
    canBook,
    canApprove,
    needsInvite,
    hasAgreedToCoc,
    canManageInventory,
    canViewInventory,
    canUpdateConsumables,
    checkAuth,
    logout,
    redeemInvite,
    loginWithGoogle,
    loginWithFacebook,
    loginWithGitHub,
    loginWithDiscord,
    agreeToCoc,
  }
})
